import {Component, OnDestroy, OnInit} from '@angular/core';
import {UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators} from '@angular/forms';
import {ActivatedRoute, Router} from '@angular/router';
import {SurveyInformation} from './survery-information';
import {SurveryEvaluationService} from '../survey-thirtyeighty/survery-evaluation.service';
import {AlertService} from '../../services/alert/alert.service';
import {LoadingService} from '../../services/loading/loading.service';
import {distinctUntilChanged, filter, shareReplay, switchMap, takeWhile, tap} from 'rxjs/operators';
import {StateCountryService} from '../../services/state-country/state-country.service';
import {BehaviorSubject, Observable, ReplaySubject} from 'rxjs';
import {AgencyMaster, StateCountry} from '../../shared/common-components/common-structure';
import {TypeaheadMatch} from 'ngx-bootstrap/typeahead';
import {BsModalRef, BsModalService} from 'ngx-bootstrap/modal';
import {RatesSroMasterService} from '../../services/rates-sro-master/rates-sro-master.service';
import {CommonValidators} from '../../shared/common-components/common-validators';
import AppConstants, {api_error} from '../../shared/common-components/AppConstants';
import { HttpErrorResponse } from '@angular/common/http';
import {ConfirmationModalComponent} from 'src/app/shared/common-components/confirmation-modal/confirmation-modal.component';
import {CustomerRegistrationService} from '../../services/customer-registration/customer-registration.service';
import moment from 'moment';

@Component({
    selector: 'app-evaluation-form',
    templateUrl: './evaluation-form.component.html',
    styleUrls: ['./evaluation-form.component.css'],
    standalone: false
})
export class EvaluationFormComponent implements OnInit, OnDestroy {
  surveyNo: string;
  agencyMaster: AgencyMaster[];
  bureauMaster: any;
  private observer$ = new BehaviorSubject(true);
  surveyInfo: SurveyInformation = null;
  private defaultCountry = new BehaviorSubject<StateCountry>(null);
  private canadaStateCountryId = new ReplaySubject<number>(1);
  evaluationForm: UntypedFormGroup;
  amcForm: UntypedFormGroup;
  // CTN may need to be added for future feature.
  bolType = [];
  bolTypes
    = [{label: 'HHG - Household Goods', value: 'HHG'},
    {label: 'CTN - Containerized', value: 'CTN'},
    {label: 'UAB - Unaccompanied Air Baggage', value: 'UAB'},
    {label: 'ICE - Internal Combustion Engine POV Vehicle', value: 'ICE'},
    {label: 'HYB - Hybrid POV Vehicle', value: 'HYB'},
    {label: 'ELE - Electric POV Vehicle', value: 'ELE'},
    {label: 'HTO - Household Goods OTO', value: 'HTO'},
    {label: 'CTO - Containerized OTO', value: 'CTO'},
    {label: 'UTO - UAB OTO', value: 'UTO'},
    {label: 'ITO - Internal Combustion OTO', value: 'ITO'},
    {label: 'YTO - Hybrid OTO', value: 'YTO'},
    {label: 'ETO - Electric OTO', value: 'ETO'},
  ];
  modalRef: BsModalRef;
  surveyMessage: any;
  headerMsg: any;
  ratingForm: UntypedFormGroup;
  adminForm: UntypedFormGroup;
  isReadOnly = false;
  showDeliveryDateError = false;
  isRatingReadOnly = false;
  isAmcReadOnly = false;
  maxDate = new Date();
  max = 5;
  adminModeButtons = false;
  showRatingForm = false;
  showAmcForm = false;
  showCarrierForm = false;
  disableForm = false;
  showAmcButtons = false;
  hideInitiateSurveyButtons = false;
  initiateSurveyComponents = true;
  showNotifyEmployeeButton = false;
  scacCodeList: any = [];
  carrierList: any;
  countries: StateCountry[];
  canadaProvinces: any[];
  dataSourceOriginSearchResults$: Observable<any>;
  dataSourceDestinationSearchResults$: Observable<any>;
  columnDefs: any;
  rowData: any;
  paginationPageSize;
  invalidScac: Boolean = false;
  domLayout;
  scac$ = this.surveyService.getScac().pipe(shareReplay(1));
  allCountriesList$ = this.stateCountryService.getPublicAllCountries().pipe(
    tap((allCountries) => {
      const defaultOrigin = allCountries.find(x => {
        return x.stateCountryCode === 'US00';
      });
      this.defaultCountry.next(defaultOrigin);
    }),
    shareReplay(1)
  );
  provincesList$ = this.canadaStateCountryId.pipe(
    distinctUntilChanged(),
    switchMap((stateId) => {
      return this.stateCountryService
        .getPublicStateCountryByParentCountryId(stateId);
    }),
    shareReplay(1)
  );
  displayNewQuestion: Boolean = false;
  testDisplay = new BehaviorSubject(this.surveyInfo);

  constructor(private fb: UntypedFormBuilder, private route: ActivatedRoute, private surveyService: SurveryEvaluationService,
              private stateCountryService: StateCountryService, private ratesSroMasterService: RatesSroMasterService,
              private alert: AlertService, private loader: LoadingService, private modalService: BsModalService,
              private router: Router, private customerRegistrationService: CustomerRegistrationService) {
    this.createEvaluationForm();
    this.createRatingForm();
    this.createAmcForm();
    this.createAdminForm();
    this.domLayout = 'autoHeight';
    this.paginationPageSize = 10;
  }

  /**
   * @description creates the evaluation and rating forms; resets the old duty and new duty station values. subscribes to default country value and if it is not null, sets the default country to new and old duty stations.
   */
  ngOnInit() {
    this.bolType = this.bolTypes;
    this.getScac();
    this.getAgencyList();
    this.resetOriginAndDestinationEvents();
    this.defaultCountry
      .pipe(filter(value => value != null))
      .pipe(takeWhile(() => this.observer$.value))
      .subscribe(defaultCountry => {
        if (this.evaluationForm.get('origin.country')) {
          this.evaluationForm
            .get('origin.country')
            .setValue(defaultCountry);
        }
        if (this.evaluationForm.get('destination.country')) {
          this.evaluationForm
            .get('destination.country')
            .setValue(defaultCountry);
        }
      });
    this.route.queryParams
      .pipe(takeWhile(() => this.observer$.value))
      .subscribe(params => {
        this.surveyNo = '';
        if (params.hasOwnProperty('adminSurveyNo')) {
          this.surveyNo = params.adminSurveyNo;
          this.loader.show();
          this.surveyService.getSurveyInformation(this.surveyNo).subscribe(res => {
            this.surveyInfo = res;
            this.testDisplay.next(this.surveyInfo);
            this.setSurveyInfo('adminSurveyNo');
            this.loader.hide();
          }, error => {
            this.loader.hide();
            this.alert.error('Error while getting survey Information.');
            this.router.navigate(['/', 'adminSurvey']);
          });
        } else if (params.hasOwnProperty('surveyNo')) {
          this.surveyNo = params.surveyNo;

          this.loader.show();
          this.surveyService.getSurveyInformation(this.surveyNo).subscribe(res => {
            this.surveyInfo = res;
            this.testDisplay.next(this.surveyInfo);
            console.log(res);
            this.setSurveyInfo('surveyNo');
            this.loader.hide();
          }, error => {
            this.loader.hide();
            this.alert.error('Error while getting survey Information.');
            this.router.navigate(['/', 'survey']);
          });
        } else if (params.hasOwnProperty('adminBol')) {
          this.surveyNo = params.adminBol;
          const isAmc = params.amc;
          this.loader.show();
          this.surveyService.getSurveyBol(this.surveyNo).subscribe(res => {
            this.surveyInfo = res;
            this.testDisplay.next(this.surveyInfo);
            this.setSurveyInfo('adminBol', isAmc);
            this.loader.hide();
          }, error => {
            this.loader.hide();
            this.alert.error('Error while getting survey Information.');
            this.router.navigate(['/', 'adminSurvey']);
          });
        } else {
          this.hideInitiateSurveyButtons = true;
        }
      });
    this.testDisplay.subscribe((val: any) => {
      if (AppConstants.isNotNullCondition(val)) {
        this.displayNewQuestion = this.displayNewQuestions(val.deliveryDate);
      }
    });
  }

  /**
   * @description updates location based on country
   */
  private updateLocation(stateCountry: StateCountry, location: string) {
    if (stateCountry.stateCountryName === 'UNITED STATES') {
      this.addUSLocation(location);
      return;
    }
    if (stateCountry.stateCountryName === 'CANADA') {
      this.canadaStateCountryId.next(stateCountry.stateCountryId);
      this.addCanadaLocation(location);
      return;
    }
    this.removeLocation(location);
  }

  /**
   * @description setting survey info based on route
   *
   */
  setSurveyInfo(typeOfSurvey, isAmc?) {
    if (this.surveyNo === undefined || this.surveyNo === null || this.surveyNo === '') {
      this.hideInitiateSurveyButtons = true;
    } else {
      this.hideInitiateSurveyButtons = false;
      this.initiateSurveyComponents = false;
      if (AppConstants.isNotNullCondition(this.surveyInfo)) {
        if (this.surveyInfo.status === 'Deleted') {
          this.loader.hide();
          this.alert.information('The survey number you are trying to access is deleted!');
          switch (typeOfSurvey) {
            case 'adminSurveyNo':
            case 'adminBol':
              this.router.navigate(['/', 'adminSurvey']);
              break;
            default:
              this.router.navigate(['/', 'survey']);
              break;
          }
          return;
        }

        if (typeOfSurvey === 'adminSurveyNo' || (typeOfSurvey === 'adminBol' && isAmc !== 'true')) {
          if (AppConstants.isNotNullCondition(this.surveyInfo)) {
            this.loadEvaluationFormData(this.surveyInfo);
            this.loadAdminForm(this.surveyInfo);
          }
          if (AppConstants.isNotNullCondition(this.surveyInfo.hhg3080ResponseEntity)) {
            this.loadRatingForm(this.surveyInfo.hhg3080ResponseEntity);
          }
          this.loadAmcForm(this.surveyInfo);
          this.initiateSurveyComponents = false;
          this.showCarrierForm = true;
          this.isReadOnly = false;
          this.showRatingForm = true;
          this.isRatingReadOnly = false;
          this.disableForm = true;
          this.showAmcForm = true;
          this.showAmcButtons = true;
          this.adminModeButtons = true;
          this.amcForm.disable();
        } else if (this.surveyNo.length === 17 && this.surveyNo.slice(-2) !== 'CR' || (typeOfSurvey === 'adminBol' && isAmc === 'true')) {
          if (this.surveyInfo.status === 'Pending') {
            this.loader.hide();
            this.alert.information('This survey is Pending at this time.');
            switch (typeOfSurvey) {
              case 'adminSurveyNo':
              case 'adminBol':
                this.router.navigate(['/', 'adminSurvey']);
                break;
              default:
                this.router.navigate(['/', 'survey']);
                break;
            }
          } else {
            if (AppConstants.isNotNullCondition(this.surveyInfo)) {
              this.loadEvaluationFormData(this.surveyInfo);
            }
            if (AppConstants.isNotNullCondition(this.surveyInfo.hhg3080ResponseEntity)) {
              this.loadRatingForm(this.surveyInfo.hhg3080ResponseEntity);
            }
            this.loadAmcForm(this.surveyInfo);
            this.isReadOnly = true;
            this.isRatingReadOnly = true;
            this.showRatingForm = true;
            this.showAmcForm = true;
            this.disableForm = true;
            this.showAmcButtons = this.surveyInfo.amcModificationRequest !== 0 && this.surveyInfo.amcModificationComments !== null;
          }
        } else if (this.surveyNo.length === 17 && this.surveyNo.slice(-2) === 'CR') {
          if (AppConstants.isNotNullCondition(this.surveyInfo)) {
            this.loadEvaluationFormData(this.surveyInfo);
          }
          if (AppConstants.isNotNullCondition(this.surveyInfo.hhg3080ResponseEntity)) {
            this.loadRatingForm(this.surveyInfo.hhg3080ResponseEntity);
          }
          this.isReadOnly = true;
          this.isRatingReadOnly = true;
          this.showCarrierForm = true;
          this.showRatingForm = true;
          this.disableForm = true;
        } else {
          if (this.surveyInfo.status === 'Completed') {
            this.loader.hide();
            this.alert.information('This survey has already been completed.');
            this.router.navigate(['/', 'survey']);
          } else if (this.surveyInfo.status === 'Pending') {
            // setting data after 500ms so that all calls will complete.
            if (AppConstants.isNotNullCondition(this.surveyInfo)) {
              this.loadEvaluationFormData(this.surveyInfo);
            }
            if (AppConstants.isNotNullCondition(this.surveyInfo.hhg3080ResponseEntity)) {
              this.loadRatingForm(this.surveyInfo.hhg3080ResponseEntity);
            }
            this.isReadOnly = true;
            this.isRatingReadOnly = false;
            this.isAmcReadOnly = false;
            this.showRatingForm = true;
            this.showAmcForm = false;
            this.disableForm = false;
            this.initiateSurveyComponents = false;
          }
        }
        // to set the messages dyanamically to display on the screen
        let bolTypeMsg = ' ';
        let secondSetMsg = ' ';

        if (this.surveyInfo.bolType === 'HHG') {
          bolTypeMsg = 'Household Goods (HHG)';
          secondSetMsg = 'privately owned vehicle (POV) and/or unaccompanied air baggage (UAB)';
        } else if (this.surveyInfo.bolType === 'UAB') {
          bolTypeMsg = 'Unaccompanied Air Baggage (UAB)';
          secondSetMsg = 'household goods (HHG) and/or privately owned vehicle (POV)';
        } else if (this.surveyInfo.bolType === 'CTN') {
          bolTypeMsg = 'Containerized (CTN)';
          secondSetMsg = 'household goods (HHG) and/or privately owned vehicle (POV)';
        } else if (this.surveyInfo.bolType === 'HYB') {
          bolTypeMsg = 'Hybrid POV Vehicle (HYB)';
          secondSetMsg = 'household goods (HHG) and/or privately owned vehicle (POV)';
        } else if (this.surveyInfo.bolType === 'HTO') {
          bolTypeMsg = 'Household Goods OTO (HTO)';
          secondSetMsg = 'household goods (HHG) and/or privately owned vehicle (POV)';
        } else if (this.surveyInfo.bolType === 'ELE') {
          bolTypeMsg = 'Electric POV Vehicle (ELE)';
          secondSetMsg = 'household goods (HHG) and/or privately owned vehicle (POV)';
        } else if (this.surveyInfo.bolType === 'ICE') {
          bolTypeMsg = 'Internal Combustion Engine POV Vehicle (ICE)';
          secondSetMsg = 'household goods (HHG) and/or privately owned vehicle (POV)';
        } else if (this.surveyInfo.bolType === 'UTO') {
          bolTypeMsg = 'UAB OTO (UTO)';
          secondSetMsg = 'household goods (HHG) and/or privately owned vehicle (POV)';
        } else if (this.surveyInfo.bolType === 'ITO') {
          bolTypeMsg = 'Internal Combustion OTO (ITO)';
          secondSetMsg = 'household goods (HHG) and/or privately owned vehicle (POV)';
        } else if (this.surveyInfo.bolType === 'ETO') {
          bolTypeMsg = 'Electric OTO (ETO)';
          secondSetMsg = 'household goods (HHG) and/or privately owned vehicle (POV)';
        } else if (this.surveyInfo.bolType === 'YTO') {
          bolTypeMsg = 'Hybrid OTO (YTO)';
          secondSetMsg = 'household goods (HHG) and/or privately owned vehicle (POV)';
        } else if (this.surveyInfo.bolType === 'CTO') {
          bolTypeMsg = 'Containerized OTO (CTO)';
          secondSetMsg = 'household goods (HHG) and/or privately owned vehicle (POV)';
        }

        this.headerMsg = this.surveyInfo.shipmentType + ' - ' + bolTypeMsg;
        this.surveyMessage = '<font size="4">This survey is requesting that you evaluate the shipment of your <strong>' + bolTypeMsg + '</strong> for your recent ' + this.surveyInfo.shipmentType + ' relocation.  If you also had additional shipment(s) as part of your relocation, ' +
          'you should receive additional 3080 Surveys for these components of your relocation as well. ' +
          'If you do not, please contact your Agency Move Coordinator.';
      }
    }
  }


  /**
   * @description updates USA formGroup if the country is USA and populates its controls based on the typeAhead observable subscription.
   * and removes canada location form control
   */
  private addUSLocation(fromElementName: string) {
    this.removeCanadaLocation(fromElementName);
    if (this.evaluationForm.get(fromElementName + '.usaLocation')) {
      this.evaluationForm.get(fromElementName + '.usaLocation').reset();
      return;
    }
    const fromGroup = this.evaluationForm.get(fromElementName) as UntypedFormGroup;
    fromGroup.addControl('usaLocation', this.createUSALocationFromGroup());
    if (fromElementName === 'origin') {
      this.dataSourceOriginSearchResults$ = this.createSearchStringObservable(fromGroup);
    }
    if (fromElementName === 'destination') {
      this.dataSourceDestinationSearchResults$ = this.createSearchStringObservable(fromGroup);
    }
    fromGroup.get('usaLocation.city').valueChanges.pipe(filter(value => value))
      .pipe(takeWhile(() => this.observer$.value))
      .subscribe(city => {
        fromGroup.get('usaLocation.cityName').setValue(city.cityName);
        fromGroup.get('usaLocation.state').setValue(city.stateAbbrev);
        fromGroup.get('usaLocation.zip').setValue(city.zipCode);
      });
  }

  /**
   * @description updates CANADA formGroup if the country is CANADA and populates its controls based on the typeAhead observable subscription.
   * and removes usa location form control
   */
  private addCanadaLocation(fromElementName: string) {
    this.removeUSALocation(fromElementName);
    if (this.evaluationForm.get(fromElementName + '.canadaLocation')) {
      this.evaluationForm.get(fromElementName + '.canadaLocation').reset();
      return;
    }
    const fromGroup = this.evaluationForm.get(fromElementName) as UntypedFormGroup;
    fromGroup.addControl('canadaLocation', this.createCanadaLocationFromGroup());
  }

  /**
   * @description generic method to remove form controls of coutnry formgroup
   */
  private removeLocation(fromElementName: string) {
    this.removeCanadaLocation(fromElementName);
    this.removeUSALocation(fromElementName);
  }

  /**
   * @description type ahead observable change method that calls a service to the list of locations that matches the string entered by the user
   */
  private createSearchStringObservable(fromGroup: UntypedFormGroup) {
    return Observable.create((observer: any) => {
      this.ratesSroMasterService
        .getPublicLocations(
          fromGroup.get('usaLocation.searchString').value
        )
        .pipe(takeWhile(() => this.observer$.value))
        .subscribe((result: any) => {
          observer.next(result);
        });
    });
  }

  /**
   * @description removes form controls in canada form group
   */
  private removeCanadaLocation(fromElementName: string) {
    if (this.evaluationForm.get(fromElementName + '.canadaLocation')) {
      const fromGroup = this.evaluationForm.get(fromElementName) as UntypedFormGroup;
      fromGroup.removeControl('canadaLocation');
    }
  }

  /**
   * @description removes form controls in USA form group
   */
  private removeUSALocation(fromElementName: string) {
    if (this.evaluationForm.get(fromElementName + '.usaLocation')) {
      const fromGroup = this.evaluationForm.get(fromElementName) as UntypedFormGroup;
      fromGroup.removeControl('usaLocation');
    }
  }

  /**
   * @description typeahead string change method for origin
   */
  typeaheadOriginOnSelect(e: TypeaheadMatch): void {
    if (e.item.stateAbbrev === 'AK') {
      // CTN may need to be added for future feature.
      // this.bolType = ['HHG', 'UAB'];
      this.bolType = this.bolTypes.filter(bol =>  bol.value === 'HHG' || bol.value === 'CTN' || bol.value === 'HYB' ||
        bol.value === 'HTO' || bol.value === 'ELE' ||
        bol.value === 'ICE' || bol.value === 'UTO' ||
        bol.value === 'ITO' || bol.value === 'ETO' || bol.value === 'YTO' ||
        bol.value === 'CTO'|| bol.value === 'UAB' );
    } else {
      this.bolType = this.bolTypes.filter(bol =>  bol.value === 'HHG' || bol.value === 'CTN' || bol.value === 'HYB' ||
        bol.value === 'HTO' || bol.value === 'ELE' ||
        bol.value === 'ICE' || bol.value === 'UTO' ||
        bol.value === 'ITO' || bol.value === 'ETO' || bol.value === 'YTO' ||
        bol.value === 'CTO' );
    }
    this.evaluationForm.get('origin.usaLocation.city').setValue(e.item);
  }

  /**
   * @description typeahead string change method for origin
   */
  typeaheadScacOnSelect(e: TypeaheadMatch): void {
    // this.evaluationForm.get('scac').setValue(e.item);
    this.validateScac();
  }

  /**
   * @description typeahead string change method for destination
   */
  typeaheadDestinationOnSelect(e: TypeaheadMatch): void {
    const originalCountry = this.evaluationForm.get('origin.country')?.value;
    const destinationCountry = this.evaluationForm.get('destination.country')?.value;

    if (originalCountry.stateCountryName === destinationCountry.stateCountryName &&
      originalCountry.stateCountryName === 'UNITED STATES' && destinationCountry.stateCountryName === 'UNITED STATES'
      && this.evaluationForm.get('origin.usaLocation.state')?.value !== 'AK' && e.item.stateAbbrev !== 'AK') {
      this.bolType = this.bolTypes.filter(bol =>  bol.value === 'HHG' || bol.value === 'CTN' || bol.value === 'HYB' ||
        bol.value === 'HTO' || bol.value === 'ELE' ||
        bol.value === 'ICE' || bol.value === 'UTO' ||
        bol.value === 'ITO' || bol.value === 'ETO' || bol.value === 'YTO' ||
        bol.value === 'CTO');

    } else {
      if (e.item.stateAbbrev === 'AK' || this.evaluationForm.get('origin.usaLocation.state')?.value === 'AK') {
        // CTN may need to be added for future feature.
        // this.bolType = ['HHG', 'UAB'];
        this.bolType = this.bolTypes.filter(bol =>  bol.value === 'HHG' || bol.value === 'CTN' || bol.value === 'HYB' ||
          bol.value === 'HTO' || bol.value === 'ELE' ||
          bol.value === 'ICE' || bol.value === 'UTO' ||
          bol.value === 'ITO' || bol.value === 'ETO' || bol.value === 'YTO' ||
          bol.value === 'CTO'|| bol.value === 'UAB' );
      } else {
        // CTN may need to be added for future feature.
        // this.bolType = ['HHG'];
        // this.bolType = ['HHG', 'UAB'];
        this.bolType = this.bolTypes.filter(bol =>  bol.value === 'HHG' || bol.value === 'CTN' || bol.value === 'HYB' ||
          bol.value === 'HTO' || bol.value === 'ELE' ||
          bol.value === 'ICE' || bol.value === 'UTO' ||
          bol.value === 'ITO' || bol.value === 'ETO' || bol.value === 'YTO' ||
          bol.value === 'CTO'|| bol.value === 'UAB' );
      }
    }
    this.evaluationForm.get('destination.usaLocation.city').setValue(e.item);
  }

  /**
   * @description creates origin/destination form group with corresponding controls
   */
  private addDefaultLocationFromGroup() {
    return new UntypedFormGroup({
      country: new UntypedFormControl(null, [Validators.required])
    });
  }

  /**
   * @description creates usa form group with corresponding controls
   */
  private createUSALocationFromGroup() {
    return new UntypedFormGroup({
      searchString: new UntypedFormControl(null, [Validators.required]),
      city: new UntypedFormControl(null, [Validators.required]),
      cityName: new UntypedFormControl(null, [Validators.required]),
      state: new UntypedFormControl(null, [Validators.required]),
      zip: new UntypedFormControl(null, [Validators.required]),
    });
  }

  /**
   * @description creates canada form group with corresponding controls
   */
  private createCanadaLocationFromGroup() {
    return new UntypedFormGroup({
      province: new UntypedFormControl(null, [Validators.required])
    });
  }

  /**
   * @description loads data into amc form
   */
  loadAmcForm(data) {
    this.amcForm.patchValue({
      amcModificationRequest: data.amcModificationRequest === 1,
      amcModificationComments: data.amcModificationComments
    });
  }

  /**
   * @description creates evaluationForm with respective formcontrols and formgroups
   */
  createEvaluationForm() {
    this.evaluationForm = this.fb.group({
      agency: new UntypedFormControl(null),
      bureauCode: new UntypedFormControl(''),
      employeeFname: ['', [Validators.required, Validators.maxLength(35)]],
      employeeMi: ['', [Validators.maxLength(1)]],
      employeeLname: ['', [Validators.required, Validators.maxLength(35)]],
      employeePhone: ['', [Validators.required, Validators.minLength(10), Validators.maxLength(20)]],
      employeePhoneExt: ['', [Validators.maxLength(5)]],
      employeeEmail: ['', [Validators.required, CommonValidators.email, Validators.maxLength(254)]],
      amcFname: ['', [Validators.required, Validators.maxLength(35)]],
      amcMi: ['', [Validators.maxLength(1)]],
      amcLname: ['', [Validators.required, Validators.maxLength(35)]],
      amcPhone: ['', [Validators.required, Validators.minLength(10), Validators.maxLength(20)]],
      amcPhoneExt: ['', [Validators.maxLength(5)]],
      amcEmail: ['', [Validators.required, CommonValidators.email, Validators.maxLength(254)]],
      tspContactFname: ['', [Validators.required, Validators.maxLength(35)]],
      tspContactMi: ['', [Validators.maxLength(1)]],
      tspContactLname: ['', [Validators.required, Validators.maxLength(35)]],
      tspContactPhone: ['', [Validators.required, Validators.maxLength(20), Validators.minLength(10)]],
      tspContactPhoneExt: ['', [Validators.maxLength(5)]],
      tspContactEmail: ['', [Validators.required, Validators.maxLength(254), CommonValidators.email]],
      scac: ['', [Validators.required]],
      faicCode: ['', [Validators.required, Validators.minLength(10), Validators.maxLength(10)]],
      organizationId: ['', [Validators.required]],
      pickupDate: ['', [Validators.required]],
      deliveryDate: ['', [Validators.required]],
      bolNumber: ['', [Validators.required, Validators.pattern('^[a-zA-Z0-9 \'\-]+$'), Validators.maxLength(20)]],
      bolType: [null, [Validators.required, Validators.maxLength(20)]],
      // uniqueEmployeeIdentifier: ['', [Validators.required, Validators.maxLength(25), Validators.pattern('^[a-zA-Z0-9 \'\-]+$')]],
      origin: this.addDefaultLocationFromGroup(),
      destination: this.addDefaultLocationFromGroup()
    });
  }

  onDateChange() {
    this.showDeliveryDateError = false;
    const pickUpDate = this.evaluationForm.value.pickUpDate;
    const deliveryDate = this.evaluationForm.value.deliveryDate;
    if (AppConstants.isNotNullCondition(pickUpDate) && AppConstants.isNotNullCondition(deliveryDate)) {
      if (pickUpDate.getTime() >= deliveryDate.getTime()) {
        this.showDeliveryDateError = true;
      } else {
        this.showDeliveryDateError = false;
      }
    }
  }

  /**
   * @description creates another form for amc mode only
   */
  createAmcForm() {
    this.amcForm = this.fb.group({
      amcModificationRequest: [null, [Validators.required]],
      amcModificationComments: [null, [Validators.required]]
    });
  }

  validateScac() {
    if (this.scacCodeList.indexOf(this.evaluationForm.controls['scac'].value) === -1) {
      this.invalidScac = true;
    } else {
      this.invalidScac = false;
    }
  }

  /**
   * @description filters country from the countries API
   */
  getCountry(): any {
    this.stateCountryService.getPublicAllCountries().pipe(takeWhile(() => this.observer$.value)).subscribe(res => {
      if (AppConstants.isNotNullCondition(res)) {
        this.countries = res as StateCountry[];
      }
    }, error => {
      this.alert.error('Error while getting state country list');
    });
  }

  /**
   * using patch to load form data
   */
  loadEvaluationFormData(data) {
    if (AppConstants.isNotNullCondition(data)) {
      this.evaluationForm.patchValue({
        employeeFname: data.employeeFname,
        employeeMi: data.employeeMi,
        employeeLname: data.employeeLname,
        employeePhone: data.employeePhone,
        employeePhoneExt: data.employeePhoneExt,
        employeeEmail: data.employeeEmail,
        amcFname: data.amcFname,
        amcMi: data.amcMi,
        amcLname: data.amcLname,
        amcPhone: data.amcPhone,
        amcPhoneExt: data.amcPhoneExt,
        amcEmail: data.amcEmail,
        agency: data.employeeAgencyName,
        bureauCode: data.employeeBureauName,
        tspContactFname: data.tspContactFname,
        tspContactMi: data.tspContactMi,
        tspContactLname: data.tspContactLname,
        tspContactPhone: data.tspContactPhone,
        tspContactPhoneExt: data.tspContactPhoneExt,
        tspContactEmail: data.tspContactEmail,
        faicCode: data.faicCode,
        organizationId: data.organizationId,
        pickupDate: new Date(data.pickupDate),
        deliveryDate: new Date(data.deliveryDate),
        bolNumber: data.bolNumber,
        bolType: data.bolType,
        // uniqueEmployeeIdentifier: data.uniqueEmployeeIdentifier
      });
      this.scac$
        .pipe(takeWhile(() => this.observer$.value))
        .subscribe(carrierList => {
          let carrier;
          carrier = carrierList.filter(x => x.carrierId === data.carrierId)[0];
          this.evaluationForm.get('scac').setValue(carrier !== null ? carrier.scac + '-' + carrier.scacName : '');
        });
      /*      let carrier = null;
            if (AppConstants.isNotNullCondition(this.carrierList)) {
              carrier = this.carrierList.filter(x => x.carrierId === data.carrierId)[0];
              this.evaluationForm.get('scac').setValue(carrier !== null ? carrier.scac + '-' + carrier.scacName: '');
            }
            // sometimes scac is setting null
            setTimeout( () => {
              this.evaluationForm.get('scac').setValue(carrier !== null ? carrier.scac + '-' + carrier.scacName : '');
            }, 2500);*/
      this.allCountriesList$.subscribe(res => {
        console.log(data);
        if (AppConstants.isNotNullCondition(res)) {
          this.countries = res as StateCountry[];
          let origin, destination;

          if (this.countries !== undefined && this.countries !== null) {
            origin = this.countries.find(country => {
              return country.stateCountryId === data.originCountryId;
            });
            destination = this.countries.find(country => {
              return country.stateCountryId === data.destinationCountryId;
            });
          }
          if (AppConstants.isNotNullCondition(origin)) {
            this.evaluationForm.get('origin.country').patchValue(origin);
            if (origin.stateCountryName === 'UNITED STATES') {
              this.evaluationForm.get('origin.usaLocation.city').patchValue(data.originCity);
              this.evaluationForm.get('origin.usaLocation.searchString').patchValue(data.originCity);
              this.evaluationForm.get('origin.usaLocation.cityName').patchValue(data.originCity);
              this.ratesSroMasterService.getPublicLocations(data.originCity)
                .pipe(takeWhile(() => this.observer$.value))
                .subscribe((result: any) => {
                  if (AppConstants.isNotNullCondition(result)) {
                    const state = result.filter(x => {
                      return x.uspsZipId === data.originZipId;
                    });
                    if (AppConstants.isNotNullCondition(state)) {
                      this.evaluationForm.get('origin.usaLocation.state').patchValue(state[0].stateAbbrev);
                      this.evaluationForm.get('origin.usaLocation.zip').patchValue(state[0].zipCode);
                      this.populateBolTypes(null);
                    }
                  }
                });
            }
            if (origin.stateCountryName === 'CANADA') {
              this.processCanadaCity(data, 1);
            }
          }
          if (AppConstants.isNotNullCondition(destination)) {
            this.evaluationForm.get('destination.country').patchValue(destination);
            if (destination.stateCountryName === 'UNITED STATES') {
              this.evaluationForm.get('destination.usaLocation.city').patchValue(data.destinationCity);
              this.evaluationForm.get('destination.usaLocation.searchString').patchValue(data.destinationCity);
              this.evaluationForm.get('destination.usaLocation.cityName').patchValue(data.destinationCity);
              this.ratesSroMasterService.getPublicLocations(data.destinationCity)
                .pipe(takeWhile(() => this.observer$.value))
                .subscribe((result: any) => {
                  if (AppConstants.isNotNullCondition(result)) {
                    const state = result.filter(x => {
                      return x.uspsZipId === data.destinationZipId;
                    });
                    if (AppConstants.isNotNullCondition(state)) {
                      this.evaluationForm.get('destination.usaLocation.state').patchValue(state[0].stateAbbrev);
                      this.evaluationForm.get('destination.usaLocation.zip').patchValue(state[0].zipCode);
                      this.populateBolTypes(null);
                    }
                  }
                });
            }
            if (destination.stateCountryName === 'CANADA') {
              this.processCanadaCity(data, 2);
            }
          }

          if (destination.stateCountryName !== 'CANADA' && destination.stateCountryName !== 'UNITED STATES' &&
            origin.stateCountryName !== 'CANADA' && origin.stateCountryName !== 'UNITED STATES') {
            this.populateBolTypes(null);
          }
        }
      }, error => {
        this.alert.error('Error while getting state country list');
      });
    }
  }

  populateBolTypes($event) {
    const originCountry = this.evaluationForm.get('origin.country')?.value;
    const originLocation = this.evaluationForm.get('origin.usaLocation')?.value;
    const destinationCountry = this.evaluationForm.get('destination.country')?.value;
    const destinationLocation = this.evaluationForm.get('destination.usaLocation')?.value;
    const deliveryDate = this.evaluationForm.get('deliveryDate')?.value;
    if (originCountry && destinationCountry && originLocation && destinationLocation &&
      originCountry.stateCountryName && destinationCountry.stateCountryName && originLocation.state && destinationLocation.state) {
      if (originCountry.stateCountryName === 'UNITED STATES' && destinationCountry.stateCountryName === 'UNITED STATES') {
        // CTN may need to be added for future feature.
        // this.bolType = ['HHG'];
        if (moment(moment(new Date(deliveryDate))).isAfter(new Date('04-01-2024'))) {
          if (originLocation.state === 'AK' || destinationLocation.state === 'AK') {
            this.bolType = this.bolTypes.filter(bol => bol.value === 'HHG' || bol.value === 'CTN' || bol.value === 'HYB' ||
              bol.value === 'HTO' || bol.value === 'ELE' ||
              bol.value === 'ICE' || bol.value === 'UTO' ||
              bol.value === 'ITO' || bol.value === 'ETO' || bol.value === 'YTO' ||
              bol.value === 'CTO' || bol.value === 'UAB');
          } else {
            this.bolType = this.bolTypes.filter(bol => bol.value === 'HHG' || bol.value === 'CTN' || bol.value === 'HYB' ||
              bol.value === 'HTO' || bol.value === 'ELE' ||
              bol.value === 'ICE' || bol.value === 'UTO' ||
              bol.value === 'ITO' || bol.value === 'ETO' || bol.value === 'YTO' ||
              bol.value === 'CTO');
          }
        } else {
          if (originLocation.state === 'AK' || destinationLocation.state === 'AK') {
            this.bolType = this.bolTypes.filter(bol => bol.value === 'HHG' || bol.value === 'CTN' || bol.value === 'HYB' ||
              bol.value === 'HTO' || bol.value === 'ELE' ||
              bol.value === 'ICE' || bol.value === 'UTO' ||
              bol.value === 'ITO' || bol.value === 'ETO' || bol.value === 'YTO' ||
              bol.value === 'CTO' || bol.value === 'UAB');
          } else {
            this.bolType = this.bolTypes.filter(bol => bol.value === 'HHG' || bol.value === 'CTN' || bol.value === 'HYB' ||
              bol.value === 'HTO' || bol.value === 'ELE' ||
              bol.value === 'ICE' || bol.value === 'UTO' ||
              bol.value === 'ITO' || bol.value === 'ETO' || bol.value === 'YTO' ||
              bol.value === 'CTO');
          }
        }

      } else {
        // this.bolType = ['HHG', 'UAB'];
        if (moment(moment(new Date(deliveryDate))).isAfter(new Date('04-01-2024'))) {
          this.bolType = this.bolTypes.filter(bol => bol.value === 'HHG' || bol.value === 'CTN' || bol.value === 'HYB' ||
            bol.value === 'HTO' || bol.value === 'ELE' ||
            bol.value === 'ICE' || bol.value === 'UTO' ||
            bol.value === 'ITO' || bol.value === 'ETO' || bol.value === 'YTO' ||
            bol.value === 'CTO' || bol.value === 'UAB');
        } else {
          this.bolType = this.bolTypes.filter(bol => bol.value === 'HHG' || bol.value === 'CTN' || bol.value === 'HYB' ||
            bol.value === 'HTO' || bol.value === 'ELE' ||
            bol.value === 'ICE' || bol.value === 'UTO' ||
            bol.value === 'ITO' || bol.value === 'ETO' || bol.value === 'YTO' ||
            bol.value === 'CTO' || bol.value === 'UAB');
        }

      }
    } else {
      if (moment(moment(new Date(deliveryDate))).isAfter(new Date('04-01-2024'))) {
        this.bolType = this.bolTypes.filter(bol => bol.value === 'HHG' || bol.value === 'CTN' || bol.value === 'HYB' ||
          bol.value === 'HTO' || bol.value === 'ELE' ||
          bol.value === 'ICE' || bol.value === 'UTO' ||
          bol.value === 'ITO' || bol.value === 'ETO' || bol.value === 'YTO' ||
          bol.value === 'CTO' || bol.value === 'UAB');
      } else {
        this.bolType = this.bolTypes.filter(bol => bol.value === 'HHG' || bol.value === 'CTN' || bol.value === 'HYB' ||
          bol.value === 'HTO' || bol.value === 'ELE' ||
          bol.value === 'ICE' || bol.value === 'UTO' ||
          bol.value === 'ITO' || bol.value === 'ETO' || bol.value === 'YTO' ||
          bol.value === 'CTO' || bol.value === 'UAB');
      }
    }

  }


  processCanadaCity(hhgSurvey: any, endpoint: Number) {
    this.provincesList$.subscribe((value: any) => {
      this.canadaProvinces = value;
      let canadaProvince;
      let selectedProvince = endpoint === 1 ? hhgSurvey.originCity : hhgSurvey.destinationCity;
      canadaProvince = this.canadaProvinces.find(province => {
        return province.stateCountryName === selectedProvince;
      });
      if (AppConstants.isNotNullCondition(canadaProvince)) {
        if (endpoint === 1) {
          this.evaluationForm.get('origin.canadaLocation.province').patchValue(canadaProvince);
          this.evaluationForm.get('origin.canadaLocation.province').disable();
          this.evaluationForm.updateValueAndValidity();
        }
        if (endpoint === 2) {
          this.evaluationForm.get('destination.canadaLocation.province').patchValue(canadaProvince);
          this.evaluationForm.get('destination.canadaLocation.province').disable();
          this.evaluationForm.updateValueAndValidity();
        }
      }
    });
  }

  /**
   * @description loads data into rating form
   */
  loadRatingForm(data) {
    this.ratingForm.patchValue({
      qualityOfPacking: data.qualityOfPacking ? data.qualityOfPacking : 0,
      damageToItems: data.damageToItems ? data.damageToItems : 0,
      courtesyWorkers: data.courtesyWorkers ? data.courtesyWorkers : 0,
      delivPickupOnTime: data.delivPickupOnTime ? data.delivPickupOnTime : 0,
      clearCommunication: data.clearCommunication ? data.clearCommunication : 0,
      responsiveInProblems: data.responsiveInProblems ? data.responsiveInProblems : 0,
      overallQualityService: data.overallQualityService ? data.overallQualityService : 0,
      commentsRemarks: data.commentsRemarks ? data.commentsRemarks : 0
    });
  }

  /**
   * @description creates rating form group with corresponding controls
   */
  createRatingForm() {
    this.ratingForm = this.fb.group({
      qualityOfPacking: [null, [Validators.required]],
      damageToItems: [null, [Validators.required]],
      courtesyWorkers: [null, [Validators.required]],
      delivPickupOnTime: [null, [Validators.required]],
      clearCommunication: [null, [Validators.required]],
      responsiveInProblems: [null, [Validators.required]],
      overallQualityService: [null, [Validators.required]],
      commentsRemarks: [null]
    });
  }

  /**
   * @description creates admin form group with corresponding controls
   */
  createAdminForm() {
    this.adminForm = this.fb.group({
      adminComments: [null, [Validators.required]]
    });
  }

  /**
   * @description loads data into rating form
   */
  loadAdminForm(data) {
    if (data.status == 'Pending') {
      this.showNotifyEmployeeButton = true;
    }
    this.adminForm.patchValue({
      adminComments: data.adminComments ? data.adminComments : '',
    });
  }

  /**
   * @description returns the state of form control in any form group based on validity and state.
   */
  isFormControlInValid(formControl: string) {
    const fromElement = this.evaluationForm.get(formControl);
    return fromElement && fromElement.invalid &&
      fromElement.touched;
  }

  /**
   * @description resets the evaluation fromgroup
   */
  resetEvaluationForm() {
    this.evaluationForm.reset();
  }

  /**
   * @description to go back to the Survey Initiate page
   */
  backToSurvey() {
    // this.router.navigate(['/', 'survey']);
    window.history.back();
  }

  /**
   * @description if it is an existing survey disables the evaluation form and hides the initiate button group. if it is a new survey calls the API to save the initial survey information
   */
  initiateSurvey(form?: UntypedFormGroup) {
    this.loader.show();
    this.surveyService.initiateSurvey(this.getSurveyRequest()).subscribe(res => {
      this.isReadOnly = true;
      this.hideInitiateSurveyButtons = false;
      // this.showRatingForm = true;
      this.initiateSurveyComponents = false;
      this.surveyInfo = res;
      this.loader.hide();
      this.router.navigate(['/', 'survey']);
      this.alert.success('Successfully initiated the survey');
    }, error => {
      this.loader.hide();
      this.isReadOnly = false;
      this.hideInitiateSurveyButtons = true;
      this.showRatingForm = false;
      this.initiateSurveyComponents = true;
      this.alert.error(error.error);
    });
  }

  /**
   * @description when user clicks on submit, disables all the forms
   */
  submitSurvey() {
    // const payLoad = {...this.surveyInfo, ...this.ratingForm.value};
    const payLoad = this.surveyInfo;
    payLoad.hhg3080ResponseEntity = this.ratingForm.value;
    this.loader.show();
    this.surveyService.saveSurvey(payLoad).subscribe(res => {
      this.loader.hide();
      this.ratingForm.disable();
      this.disableForm = true;
      this.router.navigate(['/', 'survey']);
      this.alert.success('Successfully saved your responses');
    }, error2 => {
      this.loader.hide();
      this.disableForm = false;
      this.alert.error('Unable to save the survey responses. Please contact system administrator.');
    });
  }

  /**
   * @description prepare payload for submit response
   */
  private getSurveyRequest(): SurveyInformation {
    let scac;
    let request = new SurveyInformation();
    const {origin, destination, ...evaluationFormValue} = this.evaluationForm.value;
    request = {...request, ...evaluationFormValue};
    request.pickupDate = this.evaluationForm.value.pickupDate.toISOString();
    request.deliveryDate = this.evaluationForm.value.deliveryDate.toISOString();
    request.originZipId = origin && origin.usaLocation ? origin.usaLocation.city.uspsZipId : null;
    request.destinationZipId = destination && destination.usaLocation ? destination.usaLocation.city.uspsZipId : null;
    request.originCountryId = this.getStateCountryID(origin);
    request.destinationCountryId = this.getStateCountryID(destination);
    // request.originCity = origin && origin.usaLocation ? origin.usaLocation.city.cityName : null;
    request.originCity = origin && origin.usaLocation ?
      origin.usaLocation.city.cityName :
      origin && origin.canadaLocation ?
        origin.canadaLocation.province.stateCountryName :
        null;
    // request.destinationCity = destination && destination.usaLocation ? destination.usaLocation.city.cityName : null;
    request.destinationCity = destination && destination.usaLocation ?
      destination.usaLocation.city.cityName :
      destination && destination.canadaLocation ?
        destination.canadaLocation.province.stateCountryName :
        null;
    if (this.evaluationForm.value.scac.indexOf('-') !== -1) {
      scac = this.evaluationForm.value.scac.split('-')[0];
    } else {
      scac = this.evaluationForm.value.scac;
    }
    const carrier = this.carrierList.filter(x => x.scac.toUpperCase() === scac.toUpperCase())[0];
    request.carrierId = AppConstants.isNotNullCondition(carrier) ? carrier.carrierId : null;
    if ((request.originCountryId === 326 && request.destinationCountryId === 326) ||
        (request.originCountryId === 365 && request.destinationCountryId === 326) ||
        (request.originCountryId === 326 && request.destinationCountryId === 365)) {
      request.shipmentType = 'Domestic';
    } else {
      request.shipmentType = 'International';
    }
    if (this.evaluationForm.value.agency != null && this.evaluationForm.value.agency != '') {
      request.agencyCode = this.evaluationForm.value.agency.split('- ')[1];
    }

    return request;
  }

  /**
   * @description prepares the pay load for amc mode
   */
  submitAmcResponse() {
    const amcResponse = this.amcForm.value;
    amcResponse.amcModificationRequest = this.amcForm.value.amcModificationRequest ? 1 : 0;
    // const payLoad = {...this.surveyInfo, ...amcResponse};
    const payLoad = {...this.surveyInfo, ...amcResponse};
    payLoad.hhg3080ResponseEntity = this.ratingForm.value;
    this.surveyService.saveSurvey(payLoad).subscribe(res => {
      this.loader.hide();
      this.amcForm.disable();
      this.showAmcButtons = true;
      this.alert.success('Successfully saved survey response');
    }, error2 => {
      this.showAmcButtons = false;
      this.loader.hide();
      this.alert.error('Unable to save the survey response. Please contact system administrator.');
    });
  }

  /**
   * @description extracts countryId for orgin and destination from the formgroups
   */
  private getStateCountryID(location: any) {
    if (!location || !location.country) {
      return null;
    }
    return location.country.stateCountryId;
    // return location.country.stateCountryName.toUpperCase() === 'CANADA'
    //   ? location.canadaLocation.province.stateCountryId : location.country.stateCountryId;
  }

  /**
   * @description resets the rating form.
   */
  resetRatingForm() {
    this.ratingForm.reset();
  }

  /**
   * @description restes amc form fields
   */
  resetAmcForm() {
    this.amcForm.reset();
  }

  /**
   * @description This function resets events for both origin and destination from groups Initialize on init
   **/
  private resetOriginAndDestinationEvents() {
    this.evaluationForm.get('origin.country').valueChanges
      .pipe(takeWhile(() => this.observer$.value))
      .subscribe(country => {
        this.updateLocation(country, 'origin');
      });
    this.evaluationForm.get('destination.country').valueChanges
      .pipe(takeWhile(() => this.observer$.value))
      .subscribe(country => {
        this.updateLocation(country, 'destination');
      });
  }

  /**
   * @description gets the list of scac for business type HHG
   */
  private getScac() {
    this.loader.show();
    this.scac$.pipe(takeWhile(() => this.observer$.value))
      .subscribe(carrierList => {
        if (AppConstants.isNotNullCondition(carrierList)) {
          this.carrierList = carrierList;
          this.scacCodeList = carrierList.map(carrier => carrier.scac);
        }
        this.loader.hide();
      }, error => {
        this.loader.hide();
        this.alert.error('There was an error retrieving HHG TSP SCAC List');
      });
  }

  resetRatingResponse() {
    const payLoad = this.prepareAdminPayload();
    this.loader.show();
    this.surveyService.surveyAction('reset', payLoad).subscribe(res => {
      this.surveyInfo = res;
      this.ratingForm.reset();
      this.loader.hide();
      this.alert.success('successfully reset!');
    }, (error: HttpErrorResponse) => {
      this.loader.hide();
      const messages = AppConstants.extractErrorMessagesFromErrorResponse(error);
      this.alert.information(messages[0]);
    });
  }

  prepareAdminPayload() {
    const amcResponse = this.amcForm.value;
    amcResponse.amcModificationRequest = this.amcForm.value.amcModificationRequest ? 1 : 0;
    const evaluationFormPayload = this.getSurveyRequest();
    this.surveyInfo.employeeFname = evaluationFormPayload.employeeFname;
    this.surveyInfo.employeeMi = evaluationFormPayload.employeeMi;
    this.surveyInfo.employeeLname = evaluationFormPayload.employeeLname;
    this.surveyInfo.employeePhone = evaluationFormPayload.employeePhone;
    this.surveyInfo.employeePhoneExt = evaluationFormPayload.employeePhoneExt;
    this.surveyInfo.employeeEmail = evaluationFormPayload.employeeEmail;
    this.surveyInfo.amcFname = evaluationFormPayload.amcFname;
    this.surveyInfo.amcMi = evaluationFormPayload.amcMi;
    this.surveyInfo.amcLname = evaluationFormPayload.amcLname;
    this.surveyInfo.amcPhone = evaluationFormPayload.amcPhone;
    this.surveyInfo.amcPhoneExt = evaluationFormPayload.amcPhoneExt;
    this.surveyInfo.amcEmail = evaluationFormPayload.amcEmail;
    this.surveyInfo.tspContactFname = evaluationFormPayload.tspContactFname;
    this.surveyInfo.tspContactMi = evaluationFormPayload.tspContactMi;
    this.surveyInfo.tspContactLname = evaluationFormPayload.tspContactLname;
    this.surveyInfo.tspContactPhone = evaluationFormPayload.tspContactPhone;
    this.surveyInfo.tspContactPhoneExt = evaluationFormPayload.tspContactPhoneExt;
    this.surveyInfo.tspContactEmail = evaluationFormPayload.tspContactEmail;
    this.surveyInfo.faicCode = evaluationFormPayload.faicCode;
    this.surveyInfo.organizationId = evaluationFormPayload.organizationId;
    this.surveyInfo.pickupDate = evaluationFormPayload.pickupDate;
    this.surveyInfo.deliveryDate = evaluationFormPayload.deliveryDate;
    this.surveyInfo.bolNumber = evaluationFormPayload.bolNumber;
    this.surveyInfo.bolType = evaluationFormPayload.bolType;
    // this.surveyInfo.uniqueEmployeeIdentifier = evaluationFormPayload.uniqueEmployeeIdentifier;
    const payLoad = {...this.surveyInfo, ...amcResponse, ...this.adminForm.value};
    payLoad.hhg3080ResponseEntity = this.ratingForm.value;
    return payLoad;
  }

  modifySurvey() {
    const payLoad = this.prepareAdminPayload();
    this.loader.show();
    this.surveyService.surveyAction('modify', payLoad).subscribe(res => {
      this.surveyInfo = res;
      this.loader.hide();
      this.alert.success('successfully modified!');
    }, (error: HttpErrorResponse) => {
      this.loader.hide();
      const messages = AppConstants.extractErrorMessagesFromErrorResponse(error);
      this.alert.information(messages[0]);
    });
  }

  deleteSurvey() {
    const payLoad = this.prepareAdminPayload();
    this.modalRef = this.modalService.show(ConfirmationModalComponent);
    this.modalRef.content.question = 'Are you Sure, Do you want to delete the survey ' + (this.surveyNo ? this.surveyNo : '') + ' ? ';
    this.modalRef.content.title = 'Confirmation';
    this.modalRef.content.confirmText = 'Yes';
    this.modalRef.content.cancelText = 'No';
    this.modalRef.content.emitData.subscribe(result => {
      this.loader.show();
      this.surveyService.surveyAction('delete', payLoad).subscribe(res => {
        this.loader.hide();
        this.alert.success('successfully deleted!');
        this.router.navigate(['/', 'adminSurvey']);
      }, (error: HttpErrorResponse) => {
        this.loader.hide();
        const messages = AppConstants.extractErrorMessagesFromErrorResponse(error);
        this.alert.information(messages[0]);
      });
    }, (error: HttpErrorResponse) => {
      this.loader.hide();
      const messages = AppConstants.extractErrorMessagesFromErrorResponse(error);
      this.alert.information(messages[0]);
    });

  }

  notifyEmployee() {
    const payLoad = this.prepareAdminPayload();
    this.modalRef = this.modalService.show(ConfirmationModalComponent);
    this.modalRef.content.question = 'Are you Sure, Do you want to send 3080 Employee Notification ? ';
    this.modalRef.content.title = 'Sending Employee Notification Emails';
    this.modalRef.content.confirmText = 'Yes';
    this.modalRef.content.cancelText = 'No';
    this.modalRef.content.emitData.subscribe(result => {
      if (result.obj === true) {
        this.loader.show();
        this.surveyService.surveyAction('notify', payLoad).subscribe(res => {
          this.surveyInfo = res;
          this.loader.hide();
          this.alert.success('Employee email notification has been sent out!');
        }, (error: HttpErrorResponse) => {
          this.loader.hide();
          const messages = AppConstants.extractErrorMessagesFromErrorResponse(error);
          this.alert.information(messages[0]);
        });
      }
    });

  }

  showModificationHistory(createModal) {
    // Open a table to show the modification histroy
    this.loader.show();
    this.surveyService.surveyHistory(this.surveyInfo.hhg3080SurveyId).subscribe(res => {
      createModal.show();
      this.loader.hide();
      // TODO show result in modal
      this.columnDefs = [
        {headerName: 'Update Date', field: 'updateDate', filter: true, sortable: true},
        {headerName: 'Field Name', field: 'fieldName', filter: true, sortable: true},
        {headerName: 'Old Value', field: 'oldValue', filter: true, sortable: true},
        {headerName: 'New Value', field: 'newValue', filter: true, sortable: true},
        {headerName: 'User ID', field: 'updateEmailAddress', filter: true, sortable: true}
      ];

      this.rowData = res;
    }, (error: HttpErrorResponse) => {
      this.loader.hide();
      const messages = AppConstants.extractErrorMessagesFromErrorResponse(error);
      this.alert.information(messages[0]);
    });
  }

  selectACountry($event) {
    console.log($event);
    const originalCountry = this.evaluationForm.get('origin.country')?.value;
    const destinationCountry = this.evaluationForm.get('destination.country')?.value;
    const deliveryDate = this.evaluationForm.get('deliveryDate')?.value;
    if (originalCountry && destinationCountry && originalCountry.stateCountryName === destinationCountry.stateCountryName
      && originalCountry.stateCountryName === 'UNITED STATES'
      && destinationCountry.stateCountryName === 'UNITED STATES') {
      // CTN may need to be added for future feature.
      // this.bolType = ['HHG'];
      if (moment(moment(new Date(deliveryDate))).isAfter(new Date('04-01-2024'))) { //
        this.bolType = this.bolTypes.filter(bol =>  bol.value === 'HHG' || bol.value === 'CTN' || bol.value === 'HYB' ||
          bol.value === 'HTO' || bol.value === 'ELE' ||
          bol.value === 'ICE' || bol.value === 'UTO' ||
          bol.value === 'ITO' || bol.value === 'ETO' || bol.value === 'YTO' ||
          bol.value === 'CTO');
      } else {
        this.bolType = this.bolTypes.filter(bol =>  bol.value === 'HHG' || bol.value === 'CTN' || bol.value === 'HYB' ||
          bol.value === 'HTO' || bol.value === 'ELE' ||
          bol.value === 'ICE' || bol.value === 'UTO' ||
          bol.value === 'ITO' || bol.value === 'ETO' || bol.value === 'YTO' ||
          bol.value === 'CTO');
      }

    } else {
      // this.bolType = ['HHG', 'UAB'];
      if (moment(moment(new Date(deliveryDate))).isAfter(new Date('04-01-2024'))) {
        this.bolType = this.bolTypes.filter(bol =>  bol.value === 'HHG' || bol.value === 'CTN' || bol.value === 'HYB' ||
          bol.value === 'HTO' || bol.value === 'ELE' ||
          bol.value === 'ICE' ||  bol.value === 'UTO' ||
          bol.value === 'ITO' || bol.value === 'ETO' || bol.value === 'YTO' ||
          bol.value === 'CTO' || bol.value === 'UAB' );
      } else {
        this.bolType = this.bolTypes.filter(bol =>  bol.value === 'HHG' || bol.value === 'CTN' || bol.value === 'HYB' ||
          bol.value === 'HTO' || bol.value === 'ELE' ||
          bol.value === 'ICE' || bol.value === 'UTO' ||
          bol.value === 'ITO' || bol.value === 'ETO' || bol.value === 'YTO' ||
          bol.value === 'CTO' || bol.value === 'UAB' );
      }

    }
  }

  /*
*  Agency is selected, populatate the Bureau
*/
  changeAgency(event: any) {
    this.evaluationForm.get('agency').setValue(event.item.agencyBureauName + ' - ' + event.item.agencyCode);
    if (event.item.agencyCode !== null) {
      this.getBureauList(event.item.agencyCode);
    }
  }

  /*
* Getting Bureau drop down
*/
  getBureauList(agencyCode) {
    this.loader.show();
    const bureauMaster = [];
    this.customerRegistrationService.getAllBureauForAgency(agencyCode).subscribe(
      bureaus => {
        this.bureauMaster = bureaus;
        this.loader.hide();
        return this.bureauMaster;
      }, (error) => {
        this.loader.hide();
        this.alert.error('Bureau drop down did not get populated');
      });
  }

  /*getting agency drop down */
  getAgencyList() {
    this.loader.show();
    this.customerRegistrationService.getAllAgency().subscribe(
      agencys => {
        this.agencyMaster = agencys;
        this.loader.hide();
        return this.agencyMaster;
      }, (error) => {
        this.loader.hide();
        this.alert.error('Agency drop down did not get populated');
      });
  }

  displayNewQuestions(val: any) {
    //Before deployment , un-comment the bellow line
    return (moment(new Date('01-01-2023')).isAfter(moment(new Date(val))));

    //when going to live, this line needs to be removed
    // return (moment(new Date("11-15-2022")).isAfter(moment(new Date(val))));

  }

  updateBolType(val: any) {
    //Before deployment , un-comment the bellow line
    return (moment(new Date('01-01-2023')).isAfter(moment(new Date(val))));

    //when going to live, this line needs to be removed
    // return (moment(new Date("11-15-2022")).isAfter(moment(new Date(val))));

  }

  /**
   * @description setst the behaviour subject to false so that all the subscriptins will stop.
   */
  ngOnDestroy() {
    this.observer$.next(false);
    this.testDisplay.unsubscribe();
  }

}
